import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  FileText, 
  AlertCircle,
  CheckCircle,
  Loader2
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { InspectionService } from '../services/inspectionService';
import { LoadingSpinner } from '../components';
import { VideoPlayer, MarkupEditor, AudioRecorder, FramePanel } from '../components/Inspections';
import { ReportGenerator } from '../components/Inspections/ReportGenerator';
import { Inspection, InspectionFrame, FrameMarker } from '../types';

type EditorMode = 'video' | 'markup' | 'audio';

export default function InspectionEditor() {
  const { inspectionId } = useParams<{ inspectionId: string }>();
  const navigate = useNavigate();
  const { firebaseUser } = useAuth();
  
  const [inspection, setInspection] = useState<Inspection | null>(null);
  const [frames, setFrames] = useState<InspectionFrame[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  const [mode, setMode] = useState<EditorMode>('video');
  const [selectedFrameId, setSelectedFrameId] = useState<string | null>(null);
  const [selectedMarkerId, setSelectedMarkerId] = useState<string | null>(null);
  const [currentScreenshot, setCurrentScreenshot] = useState<string | null>(null);
  const [showReportGenerator, setShowReportGenerator] = useState(false);
  
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);

  const inspectionService = InspectionService.getInstance();

  // Load inspection and frames
  useEffect(() => {
    loadInspection();
  }, [inspectionId, firebaseUser]);

  const loadInspection = async () => {
    if (!inspectionId || !firebaseUser) return;

    try {
      setLoading(true);
      setError('');

      console.log('📋 Loading inspection:', inspectionId);
      
      const [inspectionData, framesData] = await Promise.all([
        inspectionService.getInspection(inspectionId),
        inspectionService.getInspectionFrames(inspectionId)
      ]);

      if (!inspectionData) {
        setError('Inspection not found');
        return;
      }

      setInspection(inspectionData);
      setFrames(framesData);
      setVideoUrl(inspectionData.videoUrl || null);

      console.log('✅ Loaded inspection:', inspectionData.name);
      console.log('✅ Loaded frames:', framesData.length);
    } catch (err: any) {
      console.error('❌ Error loading inspection:', err);
      setError(`Failed to load inspection: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Auto-save inspection updates
  const saveInspection = async (updates: Partial<Inspection>) => {
    if (!inspectionId) return;

    try {
      setSaving(true);
      await inspectionService.updateInspection(inspectionId, updates);
      setInspection(prev => prev ? { ...prev, ...updates } : null);
      showSuccess('Saved');
    } catch (err: any) {
      console.error('❌ Error saving inspection:', err);
      setError(`Failed to save: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Show success message
  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 2000);
  };

  // Handle video upload
  const handleVideoUpload = async (file: File) => {
    if (!inspectionId) return;

    try {
      setUploadingVideo(true);
      setError('');

      console.log('📤 Uploading video:', file.name);
      
      const url = await inspectionService.uploadVideo(inspectionId, file);
      setVideoUrl(url);
      
      await saveInspection({ 
        videoUrl: url, 
        videoName: file.name 
      });

      console.log('✅ Video uploaded successfully');
    } catch (err: any) {
      console.error('❌ Error uploading video:', err);
      setError(`Failed to upload video: ${err.message}`);
    } finally {
      setUploadingVideo(false);
    }
  };

  // Handle screenshot capture from video
  const handleScreenshotCapture = useCallback(async (imageData: string, timestamp: number) => {
    if (!inspectionId) return;

    try {
      console.log('📸 Processing screenshot capture...');
      
      // Convert base64 to blob
      const response = await fetch(imageData);
      const blob = await response.blob();
      
      // Upload original screenshot
      const filename = `screenshot-${Date.now()}.png`;
      const screenshotUrl = await inspectionService.uploadScreenshot(inspectionId, blob, filename);

      // Create new frame
      const newFrame: Omit<InspectionFrame, 'id' | 'createdAt'> = {
        timestamp,
        screenshotUrl,
        markupUrl: screenshotUrl, // Initially same as screenshot
        markers: [],
        order: frames.length
      };

      const frameId = await inspectionService.addFrame(inspectionId, newFrame);
      
      const frame: InspectionFrame = {
        id: frameId,
        ...newFrame,
        createdAt: new Date()
      };

      setFrames(prev => [...prev, frame]);
      setCurrentScreenshot(imageData);
      setSelectedFrameId(frameId);
      setMode('markup');

      showSuccess('Screenshot captured');
      console.log('✅ Screenshot saved as frame:', frameId);
    } catch (err: any) {
      console.error('❌ Error saving screenshot:', err);
      setError(`Failed to save screenshot: ${err.message}`);
    }
  }, [inspectionId, frames.length]);

  // Handle frame selection
  const handleFrameSelect = (frameId: string) => {
    const frame = frames.find(f => f.id === frameId);
    if (frame) {
      setSelectedFrameId(frameId);
      setCurrentScreenshot(frame.markupUrl || frame.screenshotUrl);
      setSelectedMarkerId(null);
      setMode('markup');
    }
  };

  // Handle frame deletion
  const handleFrameDelete = async (frameId: string) => {
    if (!inspectionId) return;

    try {
      await inspectionService.deleteFrame(inspectionId, frameId);
      setFrames(prev => prev.filter(f => f.id !== frameId));
      
      if (selectedFrameId === frameId) {
        setSelectedFrameId(null);
        setCurrentScreenshot(null);
        setMode('video');
      }

      showSuccess('Frame deleted');
    } catch (err: any) {
      console.error('❌ Error deleting frame:', err);
      setError(`Failed to delete frame: ${err.message}`);
    }
  };

  // Handle frame reordering
  const handleFrameReorder = async (frameIds: string[]) => {
    if (!inspectionId) return;

    try {
      await inspectionService.reorderFrames(inspectionId, frameIds);
      
      // Reorder local state
      const reorderedFrames = frameIds.map((id, index) => {
        const frame = frames.find(f => f.id === id)!;
        return { ...frame, order: index };
      });
      
      setFrames(reorderedFrames);
      showSuccess('Frames reordered');
    } catch (err: any) {
      console.error('❌ Error reordering frames:', err);
      setError(`Failed to reorder frames: ${err.message}`);
    }
  };

  // Handle markers change
  const handleMarkersChange = async (markers: FrameMarker[]) => {
    if (!inspectionId || !selectedFrameId) return;

    try {
      await inspectionService.updateFrameMarkers(inspectionId, selectedFrameId, markers);
      
      setFrames(prev => prev.map(f => 
        f.id === selectedFrameId ? { ...f, markers } : f
      ));
    } catch (err: any) {
      console.error('❌ Error updating markers:', err);
    }
  };

  // Handle marker note change
  const handleMarkerNoteChange = async (frameId: string, markerId: string, note: string) => {
    if (!inspectionId) return;

    const frame = frames.find(f => f.id === frameId);
    if (!frame) return;

    const updatedMarkers = frame.markers.map(m =>
      m.id === markerId ? { ...m, note } : m
    );

    try {
      await inspectionService.updateFrameMarkers(inspectionId, frameId, updatedMarkers);
      
      setFrames(prev => prev.map(f =>
        f.id === frameId ? { ...f, markers: updatedMarkers } : f
      ));
    } catch (err: any) {
      console.error('❌ Error updating marker note:', err);
    }
  };

  // Handle markup save
  const handleMarkupSave = async (markupImageData: string) => {
    if (!inspectionId || !selectedFrameId) return;

    try {
      // Convert base64 to blob
      const response = await fetch(markupImageData);
      const blob = await response.blob();
      
      // Upload markup
      const filename = `markup-${selectedFrameId}-${Date.now()}.png`;
      const markupUrl = await inspectionService.uploadMarkup(inspectionId, blob, filename);

      // Update frame
      await inspectionService.updateFrame(inspectionId, selectedFrameId, { markupUrl });
      
      setFrames(prev => prev.map(f =>
        f.id === selectedFrameId ? { ...f, markupUrl } : f
      ));

      showSuccess('Markup saved');
    } catch (err: any) {
      console.error('❌ Error saving markup:', err);
      setError(`Failed to save markup: ${err.message}`);
    }
  };

  // Handle audio recording complete
  const handleAudioRecordingComplete = async (audioBlob: Blob) => {
    if (!inspectionId || !selectedFrameId) return;

    try {
      const filename = `audio-${selectedFrameId}-${Date.now()}.webm`;
      const audioUrl = await inspectionService.uploadAudio(inspectionId, audioBlob, filename);

      await inspectionService.updateFrame(inspectionId, selectedFrameId, { audioUrl });
      
      setFrames(prev => prev.map(f =>
        f.id === selectedFrameId ? { ...f, audioUrl } : f
      ));

      showSuccess('Audio saved');
    } catch (err: any) {
      console.error('❌ Error saving audio:', err);
      setError(`Failed to save audio: ${err.message}`);
    }
  };

  // Handle audio delete
  const handleAudioDelete = async () => {
    if (!inspectionId || !selectedFrameId) return;

    try {
      await inspectionService.updateFrame(inspectionId, selectedFrameId, { 
        audioUrl: undefined,
        audioTranscript: undefined 
      });
      
      setFrames(prev => prev.map(f =>
        f.id === selectedFrameId ? { ...f, audioUrl: undefined, audioTranscript: undefined } : f
      ));

      showSuccess('Audio deleted');
    } catch (err: any) {
      console.error('❌ Error deleting audio:', err);
    }
  };

  // Get selected frame
  const selectedFrame = selectedFrameId ? frames.find(f => f.id === selectedFrameId) : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <main className="max-w-7xl mx-auto px-4 py-12">
          <LoadingSpinner message="Loading inspection..." color="orange" fullPage />
        </main>
      </div>
    );
  }

  if (!inspection) {
    return (
      <div className="min-h-screen bg-gray-50">
        <main className="max-w-7xl mx-auto px-4 py-12">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Inspection Not Found</h2>
            <p className="text-gray-600 mb-4">{error || 'The inspection you are looking for does not exist.'}</p>
            <button
              onClick={() => navigate('/inspections')}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
            >
              Back to Inspections
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/inspections')}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-5 h-5 mr-1" />
              Back
            </button>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">{inspection.name}</h1>
              <p className="text-sm text-gray-500">{inspection.description || 'No description'}</p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            {/* Status Indicators */}
            {saving && (
              <span className="flex items-center text-sm text-gray-500">
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Saving...
              </span>
            )}
            {successMessage && (
              <span className="flex items-center text-sm text-green-600">
                <CheckCircle className="w-4 h-4 mr-1" />
                {successMessage}
              </span>
            )}

            {/* Generate Report Button */}
            <button
              onClick={() => setShowReportGenerator(true)}
              disabled={frames.length === 0}
              className="flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FileText className="w-5 h-5 mr-2" />
              Generate Report
            </button>
          </div>
        </div>
      </header>

      {/* Error Banner */}
      {error && (
        <div className="bg-red-50 border-b border-red-200 px-4 py-3">
          <div className="flex items-center text-red-700">
            <AlertCircle className="w-5 h-5 mr-2" />
            {error}
            <button
              onClick={() => setError('')}
              className="ml-auto text-red-500 hover:text-red-700"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Video Player */}
        <div className="w-1/3 border-r border-gray-200 bg-gray-900 flex flex-col">
          <div className="p-2 bg-gray-800 text-white text-sm font-medium">
            Video Player
          </div>
          <div className="flex-1 p-2">
            {uploadingVideo ? (
              <div className="h-full flex items-center justify-center">
                <div className="text-center">
                  <Loader2 className="w-8 h-8 text-orange-500 animate-spin mx-auto mb-2" />
                  <p className="text-white">Uploading video...</p>
                </div>
              </div>
            ) : (
              <VideoPlayer
                videoUrl={videoUrl || undefined}
                onScreenshotCapture={handleScreenshotCapture}
                onVideoUpload={handleVideoUpload}
              />
            )}
          </div>
        </div>

        {/* Center Panel - Markup Editor / Audio */}
        <div className="flex-1 flex flex-col">
          {/* Mode Tabs */}
          <div className="flex border-b border-gray-200 bg-white">
            <button
              onClick={() => setMode('markup')}
              className={`px-4 py-2 text-sm font-medium ${
                mode === 'markup'
                  ? 'text-orange-600 border-b-2 border-orange-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Markup Editor
            </button>
            <button
              onClick={() => setMode('audio')}
              className={`px-4 py-2 text-sm font-medium ${
                mode === 'audio'
                  ? 'text-orange-600 border-b-2 border-orange-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Audio Commentary
            </button>
          </div>

          {/* Editor Content */}
          <div className="flex-1 overflow-hidden">
            {!selectedFrame ? (
              <div className="h-full flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <p className="mb-2">Select a frame from the right panel</p>
                  <p className="text-sm">or capture a new screenshot from the video</p>
                </div>
              </div>
            ) : mode === 'markup' ? (
              <MarkupEditor
                imageData={currentScreenshot || selectedFrame.screenshotUrl}
                markers={selectedFrame.markers}
                onMarkersChange={handleMarkersChange}
                onSave={handleMarkupSave}
                onMarkerSelect={setSelectedMarkerId}
                selectedMarkerId={selectedMarkerId}
              />
            ) : (
              <div className="h-full p-6 overflow-y-auto">
                <h3 className="text-lg font-semibold mb-4">
                  Audio Commentary - Frame {frames.findIndex(f => f.id === selectedFrameId) + 1}
                </h3>
                
                {/* Frame Preview */}
                <div className="mb-6">
                  <img
                    src={selectedFrame.markupUrl || selectedFrame.screenshotUrl}
                    alt="Frame preview"
                    className="w-full max-w-md rounded-lg border border-gray-200"
                  />
                </div>

                {/* Audio Recorder */}
                <AudioRecorder
                  existingAudioUrl={selectedFrame.audioUrl}
                  onRecordingComplete={handleAudioRecordingComplete}
                  onDelete={handleAudioDelete}
                />

                {/* Transcript Display */}
                {selectedFrame.audioTranscript && (
                  <div className="mt-6">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Transcript</h4>
                    <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
                      {selectedFrame.audioTranscript}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right Panel - Frame List */}
        <div className="w-80 border-l border-gray-200 bg-white">
          <FramePanel
            frames={frames}
            selectedFrameId={selectedFrameId}
            onFrameSelect={handleFrameSelect}
            onFrameDelete={handleFrameDelete}
            onFrameReorder={handleFrameReorder}
            onMarkerNoteChange={handleMarkerNoteChange}
          />
        </div>
      </div>

      {/* Report Generator Modal */}
      {showReportGenerator && (
        <ReportGenerator
          inspectionId={inspectionId!}
          inspection={inspection}
          frames={frames}
          onClose={() => setShowReportGenerator(false)}
          onComplete={(docUrl) => {
            setInspection(prev => prev ? { ...prev, reportDocUrl: docUrl, status: 'completed' } : null);
            setShowReportGenerator(false);
          }}
        />
      )}
    </div>
  );
}
